import {
  CartLine,
  CartLineItem,
  Discount,
  duplicateItemWithoutDiscounts,
  duplicateLineWithoutDiscounts,
  ResponseOrder,
} from "~/entities/orderEntities";
import { DiscountType, Promotion } from "~/entities/promotionEntities";
import sum from "lodash/sum";
import {
  createIndexFor,
  createLookupMapAndCheckDuplicates,
  createLookupMapFor,
} from "~/helpers/objHelpers";
import { v4 } from "uuid";
import { arraysEqualAndNoDuplicates } from "~/helpers/arrayHelpers";
import {
  getLineBeforeDiscountPrice,
  getLineBeforeDiscountUnitPrice,
} from "~/helpers/cartHelpers";
import {
  addMilliseconds,
  differenceInSeconds,
  isBefore,
  parseISO,
} from "date-fns";
import { KEYS, COMO_REDEMPTION_COOLDOWN } from "~/modules/checkout/constants";

enum PromoLineSortStrategy {
  LowestPrice,
  AlreadyHasPromo,
  LeastPromos,
}

const sortWithStrategy = (
  x: CartLine,
  y: CartLine,
  promotionCode: string,
  strategy: PromoLineSortStrategy,
  discountType: DiscountType,
): number => {
  const xDiscounts =
    discountType === DiscountType.ROOT_ITEM_DISCOUNT
      ? x.item.discounts
      : x.discounts;
  const yDiscounts =
    discountType === DiscountType.ROOT_ITEM_DISCOUNT
      ? y.item.discounts
      : y.discounts;

  switch (strategy) {
    case PromoLineSortStrategy.LowestPrice:
      return discountType === DiscountType.ROOT_ITEM_DISCOUNT
        ? x.item.unitPrice - y.item.unitPrice
        : getLineBeforeDiscountUnitPrice(x) - getLineBeforeDiscountUnitPrice(y);
    case PromoLineSortStrategy.AlreadyHasPromo:
      return (
        +yDiscounts.some((d) => d.promotionCode === promotionCode) -
        +xDiscounts.some((d) => d.promotionCode === promotionCode)
      );
    case PromoLineSortStrategy.LeastPromos:
      return (
        x.discounts.length +
        x.item.discounts.length -
        y.discounts.length -
        y.item.discounts.length
      );
  }
};

const sortItemWithStrategy = (
  x: CartLineItem,
  y: CartLineItem,
  promotionCode: string,
  strategy: PromoLineSortStrategy,
): number => {
  switch (strategy) {
    case PromoLineSortStrategy.LowestPrice:
      return x.unitPrice - y.unitPrice;
    case PromoLineSortStrategy.AlreadyHasPromo:
      return (
        +y.discounts.some((d) => d.promotionCode === promotionCode) -
        +x.discounts.some((d) => d.promotionCode === promotionCode)
      );
    case PromoLineSortStrategy.LeastPromos:
      return x.discounts.length - y.discounts.length;
  }
};

const sortLinesWithStrategies = (
  lines: CartLine[],
  promotionCode: string,
  strategies: PromoLineSortStrategy[],
  discountType: DiscountType,
): CartLine[] => {
  return [...lines].sort((x, y) => {
    for (const strategy of strategies) {
      const s = sortWithStrategy(x, y, promotionCode, strategy, discountType);
      if (s !== 0) {
        return s;
      }
    }
    return 0;
  });
};

const sortItemsWithStrategies = (
  items: CartLineItem[],
  promotionCode: string,
  strategies: PromoLineSortStrategy[],
): CartLineItem[] => {
  return [...items].sort((x, y) => {
    for (const strategy of strategies) {
      const s = sortItemWithStrategy(x, y, promotionCode, strategy);
      if (s !== 0) {
        return s;
      }
    }
    return 0;
  });
};

export const promotionMeetsOrderRestrictions = (
  order: ResponseOrder,
  promotion: Promotion,
): boolean => {
  // Check order type codes requirement
  if (
    promotion.orderTypeCodes.length &&
    !promotion.orderTypeCodes.includes(order.orderTypeCode)
  ) {
    return false;
  }

  // No need to check customer restrictions, as this is handled by the api

  return true;
};

export const orderLevelPromotionApplies = (
  order: ResponseOrder,
  lines: CartLine[],
  promotion: Promotion,
): boolean => {
  if (!promotionMeetsOrderRestrictions(order, promotion)) {
    return false;
  }

  // Small optimisation
  if (
    promotion.requiredProductCodes?.length === 0 &&
    !promotion.requiredQty &&
    !promotion.requiredPrice
  ) {
    return true;
  }

  const requiredProductCodes = new Set(promotion.requiredProductCodes);

  const requirementLines = promotion.requiredProductCodes?.length
    ? lines.filter((x) => requiredProductCodes.has(x.item.productCode))
    : lines;

  // Note there is an implicit required qty of 1 if there are required products but no requiredQty or requiredPrice
  if (requirementLines.length === 0) {
    return false;
  }

  if (promotion.requiredQty) {
    // Note do not count child products
    const actualQty = sum(requirementLines.map((x) => x.item.qty));
    if (actualQty < promotion.requiredQty) {
      return false;
    }
  }

  // Check required price
  if (promotion.requiredPrice) {
    // Note child products DO contribute here as does qty and childQty etc
    // Only unitPrice is factored however not finalUnitPrice
    const actualPrice = sum(
      requirementLines.map((x) => getLineBeforeDiscountPrice(x)),
    );
    if (actualPrice < promotion.requiredPrice) {
      return false;
    }
  }

  return true;
};

interface IdWithQty {
  id: string;
  promoQty: number;
}

interface PromotionWithQty extends Promotion {
  promoQty: number;
}

const lineMeetsPromotionRequirements = (
  line: CartLine,
  requiredMenuCodes: Set<string>,
): boolean => {
  // DIFF-FROM-DESKTOP: Don't need to worry about required pos menu codes, or lines without a menu code

  if (requiredMenuCodes.size === 0) {
    return true;
  }

  return requiredMenuCodes.has(line.menuCode);
};

export const getLinesForPromotion = (
  order: ResponseOrder,
  lines: CartLine[],
  promotion: Promotion,
): IdWithQty[] => {
  if (!promotionMeetsOrderRestrictions(order, promotion)) {
    return [];
  }

  if (promotion.productCodes.length === 0) {
    return [];
  }

  const requiredMenuCodes = new Set(promotion.menuCodes);
  lines = lines.filter((x) =>
    lineMeetsPromotionRequirements(x, requiredMenuCodes),
  );

  const requiredProductCodes = new Set(promotion.requiredProductCodes);
  const requirementLines = promotion.requiredProductCodes?.length
    ? lines.filter((x) => requiredProductCodes.has(x.item.productCode))
    : lines;

  const productCodes = new Set(promotion.productCodes);
  const applicableLines = lines.filter((x) =>
    productCodes.has(x.item.productCode),
  );

  if (applicableLines.length === 0 || requirementLines.length === 0) {
    return [];
  }

  const qtyRestrictions: number[] = [];

  // Actual max uses is increased/decreased by the qtyAdjustment
  // If the qtyAdjustment is set, it restricts the max uses per order to this amount
  if (promotion.maxUsesPerOrder) {
    qtyRestrictions.push(promotion.maxUsesPerOrder);
  }

  if (promotion.requiredQty) {
    // Note do not count child products
    const actualQty = sum(requirementLines.map((x) => x.item.qty));
    if (actualQty < promotion.requiredQty) {
      return [];
    }
    if (promotion.maxUsesPerRequirements) {
      qtyRestrictions.push(
        promotion.maxUsesPerRequirements *
          Math.floor(actualQty / promotion.requiredQty),
      );
    }
  }

  // Check required price
  if (promotion.requiredPrice) {
    // Note child products DO contribute here as does qty and childQty etc
    // Only unitPrice is factored however not finalUnitPrice
    const actualPrice = sum(
      promotion.discountType === DiscountType.ROOT_ITEM_DISCOUNT
        ? requirementLines.map((x) => x.item.unitPrice * x.item.qty)
        : requirementLines.map((x) => getLineBeforeDiscountPrice(x)),
    );
    if (actualPrice < promotion.requiredPrice) {
      return [];
    }
    if (promotion.maxUsesPerRequirements) {
      qtyRestrictions.push(
        promotion.maxUsesPerRequirements *
          Math.floor(actualPrice / promotion.requiredPrice),
      );
    }
  }

  const maxUses = !qtyRestrictions.length ? null : Math.min(...qtyRestrictions);

  if (maxUses === 0) {
    return [];
  }

  if (maxUses != null) {
    // Sort order lines by those who already have the promo first, and within those groups by discount with fewest promotions first
    // This *hopefully* results in the lowest number of instances of a line needing to be split in the case of a promo code being added to an order
    // We sort by fewest promotions first since those lines are less likely to need to split due to a different promotion later

    // We will however first prioritise placing the promotion on the cheapest lines that qualify as required by dnero users. We may want to make this configurable.
    const sortedLines = sortLinesWithStrategies(
      applicableLines,
      promotion.code,
      [
        PromoLineSortStrategy.LowestPrice,
        PromoLineSortStrategy.AlreadyHasPromo,
        PromoLineSortStrategy.LeastPromos,
      ],
      promotion.discountType,
    );

    const ret: IdWithQty[] = [];
    let qtyAdded = 0;

    for (const line of sortedLines) {
      const nextRet = {
        id: line.id,
        promoQty: Math.min(line.item.qty, maxUses - qtyAdded),
      };
      ret.push(nextRet);
      qtyAdded += nextRet.promoQty;

      if (qtyAdded >= maxUses) {
        return ret;
      }
    }

    return ret;
  }

  return applicableLines.map((x) => ({ id: x.id, promoQty: x.item.qty }));
};

const getChildItemsForPromotion = (
  order: ResponseOrder,
  children: CartLineItem[],
  promotion: Promotion,
): IdWithQty[] => {
  if (!promotionMeetsOrderRestrictions(order, promotion)) {
    return [];
  }

  if (promotion.productCodes.length === 0) {
    return [];
  }

  const requiredProductCodes = new Set(promotion.requiredProductCodes);
  const productCodes = new Set(promotion.productCodes);

  const applicableItems: CartLineItem[] = [];

  let reqItemQty = 0;
  let reqItemPrice = 0;

  let applicableLineQty = 0;

  // Loop through items collecting which ones the discount could be applied to, and storing counts of the ones that unlock the discount
  // Note we can't apply the discount to an item that has a parent item of qty != 1 above it, as it becomes impossible to split those items to apply the discount the correct number of times
  // It's fine if the item that unlocks this discount has qty > 1 since then the promotion should be unlocked that many times anyway
  const recurseChildren = (children: CartLineItem[], parentQty: number) => {
    children.forEach((child) => {
      if (parentQty === 1 && productCodes.has(child.productCode)) {
        applicableItems.push(child);
        applicableLineQty += child.qty;
      }

      const qty = child.qty * parentQty;

      if (
        promotion.requiredProductCodes.length === 0 ||
        requiredProductCodes.has(child.productCode)
      ) {
        reqItemQty += qty;
        reqItemPrice += child.unitPrice * qty;
      }

      recurseChildren(child.children, qty);
    });
  };
  recurseChildren(children, 1);

  if (applicableItems.length == 0) {
    return [];
  }

  const requiredQty = Math.max(promotion.requiredQty, 1);

  // If required qty or price are not met, don't apply
  if (reqItemQty < requiredQty || reqItemPrice < promotion.requiredPrice) {
    return [];
  }

  const qtyRestrictions: number[] = [];

  // Actual max uses is increased/decreased by the qtyAdjustment
  // If the qtyAdjustment is set, it restricts the max uses per order to this amount
  if (promotion.maxUsesPerOrder) {
    qtyRestrictions.push(promotion.maxUsesPerOrder);
  }

  if (promotion.maxUsesPerRequirements) {
    qtyRestrictions.push(
      promotion.maxUsesPerRequirements *
        Math.floor(reqItemQty / promotion.requiredQty),
    );

    // Check required price
    if (promotion.requiredPrice) {
      qtyRestrictions.push(
        promotion.maxUsesPerRequirements *
          Math.floor(reqItemPrice / promotion.requiredPrice),
      );
    }
  }

  const maxUses = !qtyRestrictions.length ? null : Math.min(...qtyRestrictions);

  if (maxUses === 0) {
    return [];
  }

  if (maxUses != null && maxUses < applicableLineQty) {
    // Sort items by those who already have the promo first, and within those groups by discount with fewest promotions first
    // This *hopefully* results in the lowest number of instances of an item needing to be split in the case of a promo code being added to an order
    // We sort by fewest promotions first since those items are less likely to need to split due to a different promotion later

    // We will however first prioritise placing the promotion on the cheapest items that qualify as required by dnero users. We may want to make this configurable.
    const sortedItems = sortItemsWithStrategies(
      applicableItems,
      promotion.code,
      [
        PromoLineSortStrategy.LowestPrice,
        PromoLineSortStrategy.AlreadyHasPromo,
        PromoLineSortStrategy.LeastPromos,
      ],
    );

    const ret: IdWithQty[] = [];
    let qtyAdded = 0;

    for (const item of sortedItems) {
      const nextRet = {
        id: item.id,
        promoQty: Math.min(item.qty, maxUses - qtyAdded),
      };
      ret.push(nextRet);
      qtyAdded += nextRet.promoQty;

      if (qtyAdded >= maxUses) {
        return ret;
      }
    }

    return ret;
  }

  return applicableItems.map((x) => ({ id: x.id, promoQty: x.qty }));
};

const filterLineDiscounts = (
  discounts: Discount[],
  linePromoMap: Record<string, Promotion | null>,
) =>
  discounts.filter((discount) => {
    // DIFF-FROM-DESKTOP: no need to check if manually added since none will be manually added
    if (discount.promotionCode == null) {
      return true;
    }

    if (linePromoMap[discount.promotionCode] != null) {
      // Same as with order above
      linePromoMap[discount.promotionCode] = null;
      return true;
    }

    return false;
  });

const generateSplitsForPromos = (
  curItemQty: number,
  promos: PromotionWithQty[],
) => {
  // Sort the promos by least allowed qty for this line first (i.e. most restrictive first)
  promos.sort((a, b) => a.promoQty - b.promoQty);

  const splits: { promotions: Promotion[]; qty: number }[] = [];
  let prevQty = 0;
  promos.forEach((promotion) => {
    // Add promo to list of promos for all splits that are more (or equally) restrictive
    splits.forEach((y) => y.promotions.push(promotion));

    // If this promo is less restrictive than the previous promos, create a new line split that is less restrictive
    if (prevQty !== promotion.promoQty) {
      splits.push({
        // Remember to remove the qty of what we already split off from the qty for this split
        // prevQty should equal sum(splits.map((x) => x.qty)) so this should be correct
        qty: promotion.promoQty - prevQty,
        promotions: [promotion],
      });
      prevQty = promotion.promoQty;
    }
  });
  // One last split might be needed if the total qty is not equal to line qty yet
  // Also if the line qty is 0 and hence we have no splits let's also add a split to preserve the line
  if (prevQty !== curItemQty || !splits.length) {
    splits.push({
      qty: curItemQty - prevQty,
      promotions: [],
    });
  }

  return splits;
};

// Returns null if unmodified, otherwise returns new value for children
const applyChildItemDiscounts = (
  item: CartLineItem,
  promosByItemId: Record<string, PromotionWithQty[]>,
): CartLineItem[] | null => {
  let res: CartLineItem[] = [];
  let resIsModified = false;

  item.children.forEach((child) => {
    const splits = generateSplitsForPromos(
      child.qty,
      promosByItemId[child.id] ?? [],
    );

    // First let's go over the original order item
    // We will assign the split with the most promotions for this
    const mainSplit = splits[0];

    const linePromoMap: Record<string, Promotion | null> = createLookupMapFor(
      mainSplit.promotions,
      (x) => x.code,
    );
    const newDiscounts = filterLineDiscounts(child.discounts, linePromoMap);
    let discountsChanged = newDiscounts.length !== child.discounts.length;
    Object.values(linePromoMap).forEach((promotion) => {
      if (promotion != null) {
        discountsChanged = true;
        newDiscounts.push(lineDiscountFromPromo(promotion));
      }
    });

    const newChildChildren = applyChildItemDiscounts(child, promosByItemId);

    if (
      discountsChanged ||
      mainSplit.qty !== child.qty ||
      newChildChildren != null
    ) {
      resIsModified = true;
      child = {
        ...child,
        qty: mainSplit.qty,
        discounts: newDiscounts,
        children: newChildChildren ?? child.children,
      };
    }

    res.push(child);

    // Add new items if needed
    for (let i = 1; i < splits.length; i += 1) {
      const newItem = duplicateItemWithoutDiscounts(child);

      newItem.discounts = splits[i].promotions.map((x) =>
        lineDiscountFromPromo(x),
      );

      newItem.qty = splits[i].qty;

      res.push(newItem);
    }

    if (splits.length > 1) {
      resIsModified = true;
    }
  });

  // Merge any items e.g. if a promotion was removed
  for (let i = res.length - 1; i >= 1; i--) {
    const item = res[i];
    if (itemMergingAllowed(item)) {
      // First index what discounts we have on this line and to what quantity
      const [itemDiscountsByCode, duplicates] =
        createLookupMapAndCheckDuplicates(
          item.discounts,
          (x) => x.promotionCode ?? "",
        );
      if (duplicates) {
        console.error(
          "New item created when applying promotions with multiple discounts with the same promotion code, merging step skipped",
        );
        continue;
      }

      // Find line to combine with from only lines with smaller index
      const indexToCombineWith = res.slice(0, i).findIndex(
        (otherItem) =>
          // Check line meets requirements to combine with this line specifically
          // No need to check unit prices in case the new line hasn't had price books applied yet, we will however check for overridden prices
          // DIFF-FROM-DESKTOP: Some filters can be removed as they are impossible + not in data structure
          otherItem.productCode === item.productCode &&
          otherItem.discounts.every(
            (x) =>
              x.promotionCode &&
              itemDiscountsByCode[x.promotionCode] &&
              itemDiscountsByCode[x.promotionCode].fixedAmount ===
                x.fixedAmount &&
              itemDiscountsByCode[x.promotionCode].percentTimes1000 ===
                x.percentTimes1000,
          ) &&
          arraysEqualAndNoDuplicates(
            item.discounts.map((x) => x.promotionCode),
            otherItem.discounts.map((x) => x.promotionCode),
          ) &&
          // Check general requirements to combine items
          itemMergingAllowed(otherItem),
      );

      if (0 <= indexToCombineWith) {
        // We can combine!
        // Simply edit this line
        const otherItem = res[indexToCombineWith];
        res[indexToCombineWith] = {
          ...otherItem,
          qty: otherItem.qty + item.qty,
        };

        // Now remove this item
        // Safe because we are looping from the highest index to lowest, so i-1 is still the next index to check
        res = res.slice(0, i).concat(...res.slice(i + 1));
        resIsModified = true;
      }
    }
  }

  if (resIsModified) {
    return res;
  }

  return null;
};

// Returns null if unmodified, otherwise returns new value for children
const calculateChildItemDiscounts = (
  order: ResponseOrder,
  line: CartLine,
  promotionsByCode: Record<string, Promotion>,
): CartLineItem[] | null => {
  // Determine which promos we have unlocked (multiple promotions could unlock the same child promotions, in which case we only apply the child promotions once)
  const childPromos: Record<string, Promotion> = {};
  line.item.discounts
    .filter(
      (discount) =>
        discount.promotionCode && promotionsByCode[discount.promotionCode],
    )
    .forEach((discount) => {
      promotionsByCode[discount.promotionCode].childPromotionCodes
        .map((promoCode) => promotionsByCode[promoCode])
        .filter((x) => !!x)
        .forEach((childPromo) => {
          childPromos[childPromo.code] = childPromo;
        });
    });

  const promosByItemId: Record<string, PromotionWithQty[]> = {};
  Object.keys(childPromos).forEach((code) => {
    const promotion = childPromos[code];

    // Ensure promotion is allowed for this line
    if (!lineMeetsPromotionRequirements(line, new Set(promotion.menuCodes))) {
      return;
    }

    // Determine which items to apply this promo to
    const applicableItems = getChildItemsForPromotion(
      order,
      line.item.children,
      promotion,
    );

    applicableItems.forEach((item) => {
      if (promosByItemId[item.id] == null) {
        promosByItemId[item.id] = [];
      }
      promosByItemId[item.id].push({ ...promotion, promoQty: item.promoQty });
    });
  });

  // Now recurse through the items and make sure they have the correct promotions
  return applyChildItemDiscounts(line.item, promosByItemId);
};

// Bundle deals are only ever formed and priced by the server. Each unit of a deal is its own line carrying one
// discount with an id of "bundle:<instanceId>:<n>". The optimistic recalculation leaves those lines untouched.
const BUNDLE_DISCOUNT_ID_PREFIX = "bundle:";

export const bundleInstanceIdFromDiscount = (discount: Discount) => {
  if (
    !discount.promotionCode ||
    !discount.id.startsWith(BUNDLE_DISCOUNT_ID_PREFIX)
  ) {
    return null;
  }
  const rest = discount.id.slice(BUNDLE_DISCOUNT_ID_PREFIX.length);
  const i = rest.lastIndexOf(":");
  return i > 0 ? rest.slice(0, i) : null;
};

export const lineInBundle = (line: { discounts: Discount[] }) =>
  line.discounts.some((x) => bundleInstanceIdFromDiscount(x) != null);

export const autoApplyDiscounts = (
  order: ResponseOrder,
  lines: CartLine[],
  scheduledPromotions: Promotion[],
): { order: ResponseOrder; lines: CartLine[] } => {
  // Setup variables to only clone input structures if a change is required
  let retOrder = order;
  const reqLines = lines;
  let retLines = reqLines;
  const retIndexById: Record<string, number> = {};
  const cloneLinesIfNeeded = () => {
    if (reqLines === retLines) {
      retLines = retLines.slice();
      retLines.forEach((line, i) => {
        retIndexById[line.id] = i;
      });
    }
  };
  const updateLine = (id: string, changes: Partial<CartLine>) => {
    cloneLinesIfNeeded();
    retLines[retIndexById[id]] = { ...retLines[retIndexById[id]], ...changes };
  };
  const addLine = (line: CartLine) => {
    cloneLinesIfNeeded();
    retLines.push(line);
    retIndexById[line.id] = retLines.length - 1;
  };

  // DIFF-FROM-DESKTOP: No need to filter active lines like in desktop as they are all active

  // DIFF-FROM-DESKTOP: Already filtered to only scheduled promotions so no need

  const promotionsByType: Record<DiscountType, Promotion[]> = createIndexFor(
    scheduledPromotions,
    (x) => x.discountType,
  );

  // We will get the order discounts first
  const applicableOrderPromos = (
    promotionsByType[DiscountType.ORDER_DISCOUNT] ?? []
  ).filter((x) => orderLevelPromotionApplies(order, lines, x));
  const orderPromoMap: Record<string, Promotion | null> = createLookupMapFor(
    applicableOrderPromos,
    (x) => x.code,
  );

  const newDiscounts = order.discounts.filter((orderDiscount) => {
    // DIFF-FROM-DESKTOP: no need to check if manually added since none will be manually added
    if (orderDiscount.promotionCode == null) {
      return true;
    }

    if (orderPromoMap[orderDiscount.promotionCode] != null) {
      // orderPromoMap[orderDiscount.code] is already applied on this order
      // we can remove it from the list to apply
      // this also prevents double applications
      orderPromoMap[orderDiscount.promotionCode] = null;
      return true;
    }

    return false;
  });
  let orderDiscountsChanged = newDiscounts.length !== order.discounts.length;
  Object.values(orderPromoMap).forEach((promotion) => {
    if (promotion != null) {
      newDiscounts.push(orderDiscountFromPromo(promotion));
      orderDiscountsChanged = true;
    }
  });
  if (orderDiscountsChanged) {
    retOrder = { ...order, discounts: newDiscounts };
  }

  // Now do the lines, other than those in a bundle deal
  const linesOutsideBundles = lines.filter((x) => !lineInBundle(x));
  // We will keep a map of the discounts each line should have
  const promosByLineId: Record<string, PromotionWithQty[]> = {};
  (promotionsByType[DiscountType.ROOT_ITEM_DISCOUNT] ?? [])
    .concat(promotionsByType[DiscountType.LINE_DISCOUNT] ?? [])
    .forEach((promotion) => {
      const applicableLines = getLinesForPromotion(
        order,
        linesOutsideBundles,
        promotion,
      );
      applicableLines.forEach((line) => {
        if (promosByLineId[line.id] == null) {
          promosByLineId[line.id] = [];
        }
        promosByLineId[line.id].push({ ...promotion, promoQty: line.promoQty });
      });
    });

  // Now we loop through the lines and check they have the right promotions
  linesOutsideBundles.forEach((line) => {
    // First calculate which splits this line is going to need (for promotions with qty restrictions)
    const splits = generateSplitsForPromos(
      line.item.qty,
      promosByLineId[line.id] ?? [],
    );

    // First let's go over the original order line
    // We will assign the split with the most promotions for this
    const mainSplit = splits[0];

    const linePromoMap: Record<string, Promotion | null> = createLookupMapFor(
      mainSplit.promotions,
      (x) => x.code,
    );
    const newLineDiscounts = filterLineDiscounts(line.discounts, linePromoMap);
    const newItemDiscounts = filterLineDiscounts(
      line.item.discounts,
      linePromoMap,
    );
    let lineDiscountsChanged =
      newLineDiscounts.length !== line.discounts.length ||
      newItemDiscounts.length !== line.item.discounts.length;
    Object.values(linePromoMap).forEach((promotion) => {
      if (promotion != null) {
        lineDiscountsChanged = true;
        if (promotion.discountType === DiscountType.ROOT_ITEM_DISCOUNT) {
          newItemDiscounts.push(lineDiscountFromPromo(promotion));
        } else {
          newLineDiscounts.push(lineDiscountFromPromo(promotion));
        }
      }
    });
    if (lineDiscountsChanged || mainSplit.qty !== line.item.qty) {
      updateLine(line.id, {
        discounts: newLineDiscounts,
        item: { ...line.item, qty: mainSplit.qty, discounts: newItemDiscounts },
      });
    }

    // Now let's add the new lines we need to add
    // Skip the first split as that was the main line so already done
    // Later on we will combine these new lines to existing lines if they have the exact same discounts + both have no children
    for (let i = 1; i < splits.length; i += 1) {
      const newLine = duplicateLineWithoutDiscounts(line);

      newLine.discounts = splits[i].promotions
        .filter((x) => x.discountType === DiscountType.LINE_DISCOUNT)
        .map((x) => lineDiscountFromPromo(x));

      newLine.item.qty = splits[i].qty;

      newLine.item.discounts = splits[i].promotions
        .filter((x) => x.discountType === DiscountType.ROOT_ITEM_DISCOUNT)
        .map((x) => lineDiscountFromPromo(x));

      addLine(newLine);
    }
  });

  // Add any child item discounts that have been unlocked by parent item discounts

  // First make a lookup map for the item discounts
  const promosByCode = createLookupMapFor(
    [
      ...(promotionsByType[DiscountType.ROOT_ITEM_DISCOUNT] ?? []),
      ...(promotionsByType[DiscountType.CHILD_ITEM_DISCOUNT] ?? []),
    ],
    (x) => x.code,
  );

  // Loop through all lines (old and new)
  retLines.forEach((line) => {
    // Get new value for children (if it needs updating)
    const newItemChildren = calculateChildItemDiscounts(
      order,
      line,
      promosByCode,
    );

    // Update if required
    if (newItemChildren != null) {
      updateLine(line.id, {
        item: { ...line.item, children: newItemChildren },
      });
    }
  });

  // Loop through new lines with no children, to see if they can be merged into existing lines
  // We always need to do this step since even with no new lines added from promotion splitting, we may have moved a promotion
  // from line B to C, now resulting in lines A and B both having no promotions, and thus lines A and B can be merged.
  // We will loop from back to front through the lines, only ever merging a line into a line earlier in the array
  // This should avoid issues arising from modifying an array whilst looping through it
  // As explained above we must even loop through lines that aren't new
  for (let i = retLines.length - 1; i >= 1; i--) {
    const line = retLines[i];
    // Filter to only lines that can / should be combined
    if (lineMergingAllowed(line)) {
      // See if we can find a prior line to combine with instead

      // First index what discounts we have on this line and to what quantity
      const [lineDiscountByCode, duplicates] =
        createLookupMapAndCheckDuplicates(
          line.discounts,
          (x) => x.promotionCode ?? "",
        );
      const [rootItemDiscountByCode, rootItemDuplicates] =
        createLookupMapAndCheckDuplicates(
          line.item.discounts,
          (x) => x.promotionCode ?? "",
        );
      if (duplicates || rootItemDuplicates) {
        console.error(
          "New line created when applying promotions with multiple discounts with the same promotion code, merging step skipped",
        );
        continue;
      }

      // Find line to combine with from only lines with smaller index
      const indexToCombineWith = retLines.slice(0, i).findIndex(
        (otherLine) =>
          // Check line meets requirements to combine with this line specifically
          // No need to check unit prices in case the new line hasn't had price books applied yet, we will however check for overridden prices
          // DIFF-FROM-DESKTOP: Some filters can be removed as they are impossible + not in data structure
          otherLine.menuCode === line.menuCode &&
          otherLine.item.productCode === line.item.productCode &&
          otherLine.discounts.every(
            (x) =>
              x.promotionCode &&
              lineDiscountByCode[x.promotionCode] &&
              lineDiscountByCode[x.promotionCode].fixedAmount ===
                x.fixedAmount &&
              lineDiscountByCode[x.promotionCode].percentTimes1000 ===
                x.percentTimes1000,
          ) &&
          otherLine.item.discounts.every(
            (x) =>
              x.promotionCode &&
              rootItemDiscountByCode[x.promotionCode] &&
              rootItemDiscountByCode[x.promotionCode].fixedAmount ===
                x.fixedAmount &&
              rootItemDiscountByCode[x.promotionCode].percentTimes1000 ===
                x.percentTimes1000,
          ) &&
          arraysEqualAndNoDuplicates(
            line.discounts.map((x) => x.promotionCode),
            otherLine.discounts.map((x) => x.promotionCode),
          ) &&
          arraysEqualAndNoDuplicates(
            line.item.discounts.map((x) => x.promotionCode),
            otherLine.item.discounts.map((x) => x.promotionCode),
          ) &&
          // Check general requirements to combine lines
          lineMergingAllowed(otherLine),
      );

      if (0 <= indexToCombineWith) {
        // We can combine!
        // Simply edit this line
        // We don't use the edit function because we already know the id + know the lines have been cloned
        const otherLine = retLines[indexToCombineWith];
        retLines[indexToCombineWith] = {
          ...otherLine,
          item: {
            ...otherLine.item,
            qty: otherLine.item.qty + line.item.qty,
          },
        };

        // Now remove this line
        // Safe because we are looping from the highest index to lowest, so i-1 is still the next index to check
        // DIFF-FROM-DESKTOP: Instead of making a line inactive sometimes we always just remove it
        retLines = retLines.slice(0, i).concat(...retLines.slice(i + 1));
      }
    }
  }

  return {
    order: retOrder,
    lines: retLines,
  };
};

export const itemMergingAllowed = (item: CartLineItem) =>
  // DIFF-FROM-DESKTOP: Some filters can be removed as they are impossible + not in data structure
  item.children.length === 0 &&
  item.product.children.length === 0 &&
  item.discounts.every((x) => x.promotionCode);

export const lineMergingAllowed = (line: CartLine) =>
  itemMergingAllowed(line.item) &&
  line.discounts.every((x) => x.promotionCode) &&
  // Each unit of a bundle deal is its own line
  !lineInBundle(line);

export const lineDiscountFromPromo = (promotion: Promotion): Discount => ({
  id: v4(),
  promotionCode: promotion.code,
  name: promotion.name,
  percentTimes1000: promotion.percentTimes1000,
  fixedAmount: promotion.fixedAmount,
});

export const orderDiscountFromPromo = (promotion: Promotion): Discount => ({
  id: v4(),
  promotionCode: promotion.code,
  name: promotion.name,
  percentTimes1000: promotion.percentTimes1000,
  fixedAmount: promotion.fixedAmount,
});

export function getCooldownMessage(promoName: string): string | null {
  const lastResetStr = localStorage.getItem(KEYS.LAST_RESET);
  if (!lastResetStr) return null;

  const lastResetTime = parseISO(lastResetStr);
  const retryAvailableAt = addMilliseconds(
    lastResetTime,
    COMO_REDEMPTION_COOLDOWN,
  );
  const now = new Date();

  if (isBefore(now, retryAvailableAt)) {
    const secondsRemaining = differenceInSeconds(retryAvailableAt, now);
    return `${promoName.toUpperCase()} on cooldown from last purchase. Please wait ${secondsRemaining} more second${
      secondsRemaining !== 1 ? "s" : ""
    } before trying again.`;
  }

  return null;
}
